import React from "react";
import { Delete, Block, CheckCircle, Info, Visibility, Warning, VerifiedUser, LinkOff } from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";
import { ContextMenuAction } from "../../components/FancyContextMenu";
import { Driver, DocStatus, MuiColor } from "./driverTypes";

interface BuildArgs {
  selectedRow: any;
  canWriteDrivers: boolean;
  canApproveDrivers: boolean;
  onViewDetails: (id: number) => void;
  onEdit: (id: number) => void;
  onSuspend: (id: number) => void;
  onActivate: (id: number) => void;
  onApprove: (driver: Driver, type: 'MANDATORY' | 'COMPLIANCE') => void;
  onShowRides: (row: any) => void;
  onShowWallet: (row: any) => void;
  onUnclaim: (driver: Driver) => void;
  onDelete: (id: number) => void;
}

/**
 * Builds the right-click context-menu actions for a driver row. The two
 * approval entries adapt their label/icon to the row's current verification
 * state (mandatory docs + compliance docs).
 */
export function buildDriverActions({
  selectedRow,
  canWriteDrivers,
  canApproveDrivers,
  onViewDetails,
  onEdit,
  onSuspend,
  onActivate,
  onApprove,
  onShowRides,
  onShowWallet,
  onUnclaim,
  onDelete,
}: BuildArgs): ContextMenuAction[] {
  // Releasing only makes sense for a driver an agent actually holds.
  const isClaimed = selectedRow?.enrolledById != null;
  const isMandatoryVerified =
      selectedRow?.isRCVerified &&
      selectedRow?.isDLVerified &&
      selectedRow?.isProfilePhotoVerified;

  const complianceDocs = [
    selectedRow?.insuranceStatus,
    selectedRow?.pucStatus,
    selectedRow?.statePermitStatus,
    selectedRow?.aitpStatus
  ];

  // Logic:
  // 1. All Verified -> "Compliance Verified"
  // 2. Not all verified, but ANY data exists (Pending, Rejected, Expired, or even Mixed Verified) -> "Review Compliance"
  // 3. All Missing -> "Compliance Docs"
  const isComplianceAllVerified = complianceDocs.every(s => s === DocStatus.VERIFIED);
  const hasSomeComplianceData = complianceDocs.some(s =>
      s === DocStatus.PENDING ||
      s === DocStatus.REJECTED ||
      s === DocStatus.EXPIRED ||
      s === DocStatus.VERIFIED
  );

  const mandatoryMenuConfig = isMandatoryVerified
      ? { text: "Driver Verified", iconColor: "success" as MuiColor, Icon: CheckCircle }
      : { text: "Approve Driver", iconColor: "warning" as MuiColor, Icon: Warning };

  const complianceMenuConfig = isComplianceAllVerified
      ? { text: "Compliance Verified", iconColor: "success" as MuiColor, Icon: VerifiedUser }
      : hasSomeComplianceData
          ? { text: "Review Pending Compliance", iconColor: "error" as MuiColor, Icon: Warning }
          : { text: "Compliance Docs", iconColor: "info" as MuiColor, Icon: Info };

  return [
    {
      dividerBefore: true,
      label: "View Full Details",
      icon: <Visibility color="primary" fontSize="small" />,
      onClick: () => {
        if (selectedRow) onViewDetails(selectedRow.id);
      },
      color: "primary",
    },
    ...(canWriteDrivers ? ([
      {
        label: "Edit / Quick View",
        icon: <Edit color="info" fontSize="small" />,
        onClick: () => onEdit(selectedRow.id),
        color: "info",
      },
      {
        label: selectedRow?.isAvailable ? "Mark UnAvailable" : "Mark Available",
        icon: selectedRow?.isAvailable
            ? <Block color="error" fontSize="small" />
            : <CheckCircle color="success" fontSize="small" />,
        onClick: () => selectedRow?.isAvailable
            ? onSuspend(selectedRow.id)
            : onActivate(selectedRow.id),
        color: selectedRow?.isAvailable ? "warning" : "success",
      },
    ] as ContextMenuAction[]) : []),
    ...(canApproveDrivers ? ([
      {
        dividerBefore: true,
        label: mandatoryMenuConfig.text,
        icon: <mandatoryMenuConfig.Icon color={mandatoryMenuConfig.iconColor} fontSize="small" />,
        onClick: () => onApprove(selectedRow, 'MANDATORY'),
        color: mandatoryMenuConfig.iconColor,
      },
      {
        label: complianceMenuConfig.text,
        icon: <complianceMenuConfig.Icon color={complianceMenuConfig.iconColor} fontSize="small" />,
        onClick: () => onApprove(selectedRow, 'COMPLIANCE'),
        color: complianceMenuConfig.iconColor,
      },
    ] as ContextMenuAction[]) : []),
    {
      dividerBefore: true,
      label: "Show Rides",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.userUid) return;
        onShowRides(selectedRow);
      },
      color: "info",
    },
    {
      label: "Show Wallet",
      icon: <Info color="info" fontSize="small" />,
      onClick: () => {
        if (!selectedRow?.userUid) return;
        onShowWallet(selectedRow);
      },
      color: "info",
    },
    // Release from the enrolling agent. Dashboard-only by construction — the ManagerApp talks to
    // /api/staff with a Firebase token and cannot reach /api/admin at all.
    ...(canWriteDrivers && isClaimed ? ([{
      dividerBefore: true,
      label: "Release from agent",
      icon: <LinkOff color="warning" fontSize="small" />,
      onClick: () => onUnclaim(selectedRow),
      color: "warning",
    }] as ContextMenuAction[]) : []),
    ...(canWriteDrivers ? ([{
      dividerBefore: true,
      label: "Delete",
      icon: <Delete color="error" fontSize="small" />,
      onClick: () => onDelete(selectedRow.id),
      color: "error",
    }] as ContextMenuAction[]) : []),
  ];
}
